import React, { createContext, useContext, useEffect, useState } from "react";
import { api, setAuthHeader, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Si l'utilisateur s'est explicitement déconnecté lors d'une session précédente,
    // on évite toute tentative de reconnexion automatique au montage. L'utilisateur
    // doit cliquer sur "Se connecter" pour ouvrir une nouvelle session.
    if (typeof window !== "undefined" && sessionStorage.getItem("plania_logged_out") === "1") {
      setUser(false);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setAuthHeader(data.access_token);
      // Connexion réussie via le bouton "Se connecter" → on lève le verrou logout.
      try { sessionStorage.removeItem("plania_logged_out"); } catch { /* noop */ }
      setUser(data.user);
      return { success: true };
    } catch (e) {
      // Distinction explicite « erreur réseau » vs « erreur métier ».
      // Sans cette distinction, axios renvoie `e.response = undefined` lors d'un
      // problème CORS / timeout / proxy → l'utilisateur voyait juste « Une erreur
      // est survenue » alors qu'on peut afficher un message bien plus utile.
      let error;
      if (e.response?.data?.detail) {
        error = formatApiError(e.response.data.detail);
      } else if (!e.response || e.code === "ERR_NETWORK" || e.code === "ECONNABORTED") {
        error = "Connexion au serveur impossible. Vérifiez votre réseau ou réessayez dans quelques secondes.";
      } else {
        error = e.message || "Une erreur est survenue.";
      }
      return { success: false, error };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setAuthHeader(null);
    // Marque la déconnexion : tant que ce flag est posé, AuthProvider ne tentera plus
    // d'auto-reconnecter au refresh. Le flag est levé au prochain login explicite.
    try { sessionStorage.setItem("plania_logged_out", "1"); } catch { /* noop */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
